<template>
  <ActionMenu class="custom-list-menu" :position="position">
    <Action
      class="custom-list-menu__btn--rename"
      icon="iedit"
      @click.prevent="customListForm?.show"
    >
      Rename
    </Action>
    <Action
      class="custom-list-menu__btn--delete"
      icon="idelete"
      @click.prevent="deletionPopup?.show"
    >
      Delete
    </Action>
  </ActionMenu>

  <Teleport to="#appRoot">
    <CustomListForm ref="customListForm" :view="renameFormView" />

    <ConfirmationPopup
      ref="deletionPopup"
      :title="`Delete List: ${list.title}`"
      :onCancel="() => true"
      :onConfirm="deleteList"
      :danger="true"
    >
      Are you sure you want to delete this list?

      <template #confirm> Delete list </template>
    </ConfirmationPopup>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import type { Raw } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import type { CustomList } from '@/models/interfaces'
import { deleteCustomList, RenameCustomListForm } from '@/models/custom.lists'
import type { CustomListsView } from '@/models/custom.lists'

import ActionMenu from '@/app/components/ActionMenu.vue'
import Action from '@/app/components/ActionMenuItem.vue'
import ConfirmationPopup from '@/components/ui/ConfirmationPopup.vue'
import CustomListForm from './CustomListForm.vue'

const props = defineProps<{
  list: CustomList
  listsView?: CustomListsView
  position?: 'center' | 'right'
}>()

const customListForm = ref<InstanceType<typeof CustomListForm> | null>(null)
const deletionPopup = ref<InstanceType<typeof ConfirmationPopup> | null>(null)

const router = useRouter()
const route = useRoute()

// Check whether this is a page whose route includes the custom list
// slug. If so, return the route name.
const singleListRoute = computed(() => {
  if (route.name === 'customList') {
    return 'customList'
  }
  if (route.name === 'my_library.customList') {
    return 'my_library.customList'
  }
  return undefined
})

async function deleteList(): Promise<boolean> {
  return await deleteCustomList(
    props.list,
    !!singleListRoute.value,
    props.listsView,
  )
}

const renameFormView = ref<Raw<RenameCustomListForm>>(
  new RenameCustomListForm(props.list, router, singleListRoute.value),
)
</script>

<style lang="scss" scoped>
.custom-list-menu {
  --button-background-hover: #{rgba($c-black-2, 0.05)};
}

.night-mode .custom-list-menu {
  --button-background-hover: #{rgba($c-dark-3, 0.5)};
}
</style>
